/**
 * Deterministic booked time for a clinician-day when no real visits exist.
 * Same seed + same day + same clinician → same numbers, on every reload.
 *
 * Drive is derived from visit counts: routine visits use the branch's typical
 * inter-visit drive; assessments (new or returning patients, unfamiliar
 * addresses) use the SOC slot's drive assumption plus a first-visit allowance.
 */
import type { ConfigState, DateKey, RosterEntry, VirtualCensusState } from "@caredna-marlin/utils/homeHealthDemo/state";
import { isoWeekday } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { routineVisitTypeFor } from "@caredna-marlin/utils/homeHealthDemo/engine/visitTypes";
import { isSocCapable } from "./roster";

export interface VirtualDay {
  bookedMinutes: number;
  driveMinutes: number;
  assessments: number;
  routineVisits: number;
  points: number;
}

export const ROUTINE_DRIVE_MINUTES = 17;
export const ASSESSMENT_FIRST_VISIT_DRIVE_ALLOWANCE = 10;

const ZERO: VirtualDay = { bookedMinutes: 0, driveMinutes: 0, assessments: 0, routineVisits: 0, points: 0 };

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hash(input: string): number {
  let h = 2166136261;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** Share of shift time booked in the home, before drive, by employment type. */
const BASE_UTILIZATION = { FT: 0.53, PT: 0.5, perDiem: 0.45 } as const;

export function assessmentDriveMinutes(config: ConfigState): number {
  return config.socSlot.driveMinutes + ASSESSMENT_FIRST_VISIT_DRIVE_ALLOWANCE;
}

export function virtualDay(
  census: VirtualCensusState,
  config: ConfigState,
  entry: RosterEntry,
  dateKey: DateKey,
  shiftMinutes: number,
): VirtualDay {
  const override = census.overrides[`${entry.handle}:${dateKey}`];
  if (shiftMinutes <= 0 && !override) return ZERO;

  const rand = mulberry32((census.seed ^ hash(`${entry.handle}:${dateKey}`)) >>> 0);
  const weekday = isoWeekday(dateKey);
  const dayFactor = weekday === 1 || weekday === 5 ? 0.92 : weekday >= 6 ? 0.75 : 1.0;
  const jitter = 0.85 + rand() * 0.3; // 0.85–1.15
  const utilization = Math.min(0.85, BASE_UTILIZATION[entry.employment] * dayFactor * jitter);

  const routine = routineVisitTypeFor(config, entry.discipline);
  const routineMinutes = routine.plannedMinutes;
  const socMinutes = config.visitTypes.SOC.plannedMinutes;

  const assessments = override?.assessments ?? (shiftMinutes > 0 && isSocCapable(entry, dateKey) && rand() < 0.45 ? 1 : 0);
  let routineVisits: number;
  if (override?.routineVisits !== undefined) {
    routineVisits = override.routineVisits;
  } else {
    const targetBooked = Math.round((shiftMinutes * utilization) / 15) * 15;
    routineVisits = Math.max(0, Math.round((targetBooked - assessments * socMinutes) / routineMinutes));
  }
  const bookedMinutes = override?.bookedMinutes ?? assessments * socMinutes + routineVisits * routineMinutes;
  const driveMinutes = override?.driveMinutes ?? assessments * assessmentDriveMinutes(config) + routineVisits * ROUTINE_DRIVE_MINUTES;
  const points = override?.points ?? Math.round((assessments * config.visitTypes.SOC.points + routineVisits * routine.points) * 4) / 4;
  return { bookedMinutes, driveMinutes, assessments, routineVisits, points };
}
